import React, { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Edit2, RefreshCw, X } from 'lucide-react';
import { AuthContext } from '../../contexts/AuthContext';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8080';

interface TrackerRow {
  tracker_id: number;
  equipment_id: number;
  imei: string;
  install_hours: number;
  installed_at: string;
  sim_iccid: string | null;
  equipment_name: string;
  bradanna_equipment_number: string | null;
  telematics_current_hours: number | null;
  telematics_synced_at: string | null;
}

const ManageTrackers: React.FC = () => {
  const navigate = useNavigate();
  const auth = useContext(AuthContext);
  const isSuperAdmin = !!auth?.user?.is_super_admin;

  const [trackers, setTrackers] = useState<TrackerRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [editing, setEditing] = useState<TrackerRow | null>(null);
  const [meterInput, setMeterInput] = useState('');
  const [saving, setSaving] = useState(false);
  const [resultMsg, setResultMsg] = useState<string | null>(null);

  const token = (() => { try { return localStorage.getItem('bfa_token'); } catch { return null; } })();

  const loadTrackers = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE_URL}/api/equipment/trackers/list`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error(j.error || `HTTP ${res.status}`);
      }
      const j = await res.json();
      setTrackers(j.data || []);
    } catch (e: any) {
      setError(e.message || 'Failed to load trackers');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isSuperAdmin) loadTrackers();
  }, [isSuperAdmin]);

  const openEdit = (t: TrackerRow) => {
    setEditing(t);
    // Pre-fill with currently-displayed total (what's "on the meter" per BFA).
    // Operator can adjust to match what they actually see on the machine.
    const seed = t.telematics_current_hours != null ? String(Math.round(t.telematics_current_hours)) : '';
    setMeterInput(seed);
    setResultMsg(null);
  };

  const submitMeter = async () => {
    if (!editing) return;
    const v = Number(meterInput);
    if (!Number.isFinite(v) || v < 0) {
      setResultMsg('Enter a valid non-negative number');
      return;
    }
    setSaving(true);
    setResultMsg(null);
    try {
      const res = await fetch(
        `${API_BASE_URL}/api/equipment/${editing.equipment_id}/tracker/install-hours`,
        {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ meter_hours: v }),
        }
      );
      const j = await res.json();
      if (!res.ok || !j.success) {
        throw new Error(j.error || `HTTP ${res.status}`);
      }
      setResultMsg(
        `Saved. Now showing ${j.data.meter_hours} hrs ` +
          `(baseline ${j.data.new_install_hours.toFixed(1)} + runtime ${j.data.traccar_runtime_at_update.toFixed(2)})`
      );
      await loadTrackers();
      setTimeout(() => { setEditing(null); setResultMsg(null); }, 1800);
    } catch (e: any) {
      setResultMsg(`Error: ${e.message || 'save failed'}`);
    } finally {
      setSaving(false);
    }
  };

  if (!isSuperAdmin) {
    return (
      <div style={{ padding: 24 }}>
        <p style={{ color: '#dc2626' }}>Super admin only.</p>
        <button onClick={() => navigate(-1)} style={{ padding: '10px 16px', marginTop: 12 }}>Back</button>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 720, margin: '0 auto', padding: 16, paddingBottom: 80 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
        <button
          onClick={() => navigate(-1)}
          style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}
          aria-label="Back"
        >
          <ArrowLeft size={24} />
        </button>
        <h1 style={{ margin: 0, fontSize: 22, flex: 1 }}>Manage Trackers</h1>
        <button
          onClick={loadTrackers}
          disabled={loading}
          style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 8 }}
          aria-label="Refresh"
          title="Refresh"
        >
          <RefreshCw size={20} className={loading ? 'animate-spin' : ''} />
        </button>
      </div>

      <p style={{ color: '#64748b', fontSize: 14, marginTop: 0, marginBottom: 16 }}>
        Tap a tracker to set its hour-meter reading. The app subtracts the GPS runtime
        to compute a new baseline so the displayed total matches what you see on the
        machine.
      </p>

      {loading && <div style={{ padding: 24, textAlign: 'center', color: '#64748b' }}>Loading...</div>}
      {error && (
        <div style={{ padding: 12, background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, color: '#b91c1c' }}>
          {error}
        </div>
      )}

      {!loading && !error && trackers.length === 0 && (
        <div style={{ padding: 24, textAlign: 'center', color: '#64748b' }}>No active trackers.</div>
      )}

      {!loading && trackers.map(t => (
        <button
          key={t.tracker_id}
          onClick={() => openEdit(t)}
          style={{
            display: 'block',
            width: '100%',
            textAlign: 'left',
            padding: 14,
            marginBottom: 10,
            background: '#fff',
            border: '1px solid #e2e8f0',
            borderRadius: 10,
            cursor: 'pointer',
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
            <div style={{ fontWeight: 700, fontSize: 16 }}>
              {t.equipment_name}
              {t.bradanna_equipment_number ? ` (${t.bradanna_equipment_number})` : ''}
            </div>
            <Edit2 size={16} style={{ color: '#94a3b8' }} />
          </div>
          <div style={{ fontSize: 13, color: '#64748b', marginTop: 4 }}>
            IMEI {t.imei} {t.sim_iccid ? `· SIM ...${t.sim_iccid.slice(-4)}` : ''}
          </div>
          <div style={{ display: 'flex', gap: 16, marginTop: 8, fontSize: 13 }}>
            <span>
              <strong style={{ color: '#0f172a' }}>
                {t.telematics_current_hours != null
                  ? `${Number(t.telematics_current_hours).toFixed(1)} hrs`
                  : '— hrs'}
              </strong>
              <span style={{ color: '#94a3b8' }}> shown</span>
            </span>
            <span style={{ color: '#64748b' }}>
              install: {Number(t.install_hours).toFixed(1)}
            </span>
          </div>
        </button>
      ))}

      {/* Edit modal */}
      {editing && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(15,23,42,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 16,
            zIndex: 100,
          }}
          onClick={() => !saving && setEditing(null)}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              background: '#fff',
              borderRadius: 14,
              padding: 18,
              maxWidth: 420,
              width: '100%',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: 12 }}>
              <div>
                <h2 style={{ margin: 0, fontSize: 18 }}>
                  {editing.equipment_name}
                  {editing.bradanna_equipment_number ? ` (${editing.bradanna_equipment_number})` : ''}
                </h2>
                <div style={{ fontSize: 12, color: '#64748b' }}>Currently shown: {Number(editing.telematics_current_hours || 0).toFixed(1)} hrs</div>
              </div>
              <button onClick={() => !saving && setEditing(null)} style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
                <X size={20} />
              </button>
            </div>
            <label style={{ display: 'block', fontSize: 14, fontWeight: 600, marginBottom: 6 }}>
              What does the hour meter read right now?
            </label>
            <input
              type="number"
              inputMode="decimal"
              value={meterInput}
              onChange={e => setMeterInput(e.target.value)}
              placeholder="e.g. 1206"
              autoFocus
              style={{
                width: '100%',
                padding: 12,
                fontSize: 18,
                border: '1px solid #cbd5e1',
                borderRadius: 8,
                marginBottom: 12,
                boxSizing: 'border-box',
              }}
            />
            {resultMsg && (
              <div
                style={{
                  padding: 10,
                  marginBottom: 12,
                  background: resultMsg.startsWith('Error') ? '#fef2f2' : '#ecfdf5',
                  color: resultMsg.startsWith('Error') ? '#b91c1c' : '#065f46',
                  border: `1px solid ${resultMsg.startsWith('Error') ? '#fecaca' : '#a7f3d0'}`,
                  borderRadius: 8,
                  fontSize: 13,
                }}
              >
                {resultMsg}
              </div>
            )}
            <div style={{ display: 'flex', gap: 8 }}>
              <button
                onClick={() => !saving && setEditing(null)}
                disabled={saving}
                style={{
                  flex: 1,
                  padding: 12,
                  border: '1px solid #cbd5e1',
                  background: '#fff',
                  borderRadius: 8,
                  fontSize: 15,
                  cursor: 'pointer',
                }}
              >
                Cancel
              </button>
              <button
                onClick={submitMeter}
                disabled={saving || !meterInput}
                style={{
                  flex: 2,
                  padding: 12,
                  border: 'none',
                  background: saving || !meterInput ? '#94a3b8' : '#3b82f6',
                  color: '#fff',
                  borderRadius: 8,
                  fontSize: 15,
                  cursor: saving || !meterInput ? 'not-allowed' : 'pointer',
                }}
              >
                {saving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManageTrackers;
